import styled from '@emotion/styled';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import Box from '~/common/components/Box';
import Button from '~/common/components/Button';
import Text from '~/common/components/Texts/Text';
import { useUser } from '~/common/hooks/useAuthentication';
import AdrAdeProgressBar from '~/pages/ClassifyDocumentsV2/AdrAdeProgressBar';
import { StyledClassifyFooter } from '~/pages/ClassifyDocumentsV2/StyledComponents/ClassifyRightContainer.styles';
import { HomeActions } from '~/pages/Home/redux';

import { LOADER_BLURB, LOADER_MESSAGE, LOADING_STATUS } from './language.json';

const StyledBox = styled<any>(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  background-color: ${(props) => props.theme.colors?.gray[50]};
  min-height: 798px;
  height: 100%;
`;
const StyledContainer = styled<any>('form')`
  width: 100%;
  max-width: 100%;
  padding: unset;
  padding-left: 1px;
  border-left: 1px solid ${(props) => props.theme.colors?.gray[100]};
`;

const loadingBarStates = {
  navigation: {
    start: 0,
    target: 0.5,
    seconds: 1,
  },
  adr: {
    start: 0.5,
    target: 0.75,
    seconds: 5,
  },
  ade: {
    start: 0.75,
    target: 0.9,
    seconds: 30,
  },
  ['refetch-navigation']: {
    start: 0.9,
    target: 1,
    seconds: 1,
  },
  complete: {
    start: 0, // overridden dynamically with persistedPercentage
    target: 1,
    seconds: 0.8,
  },
};

type AltMessageScreenProps = Readonly<
  | {
      message: string;
      showFooter?: boolean;
      loading?: never;
    }
  | {
      message?: never;
      showFooter?: false;
      loading: 'navigation' | 'refetch-navigation' | 'adr' | 'ade' | 'complete';
    }
>;

let persistedPercentage = 0;

function useProgressBar(loading: AltMessageScreenProps['loading']) {
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const [displayPercentage, setDisplayPercentage] =
    useState(persistedPercentage);

  const setPercentage = useCallback(
    (valOrFn: number | ((prev: number) => number)) => {
      setDisplayPercentage((prev) => {
        const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
        persistedPercentage = next;
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    if (!loading) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const { start: configStart, target, seconds } = loadingBarStates[loading];
    const start = loading === 'complete' ? persistedPercentage : configStart;
    intervalRef.current && clearInterval(intervalRef.current);
    setPercentage(start);

    const fps = 120;
    const frames = Math.max(1, seconds * fps);
    const step = (target - start) / frames;
    intervalRef.current = setInterval(() => {
      setPercentage((prev) => {
        const next = prev + step;
        if (next >= target) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          return target;
        }
        return next;
      });
    }, 1_000 / fps);
  }, [loading, setPercentage]);

  if (!loading) {
    return {
      displayPercentage: 0,
      blurbText: '',
      loadingStatus: null,
    };
  }

  const blurbText =
    loading === 'navigation' ? LOADER_MESSAGE.DEFAULT : LOADER_BLURB;
  const loadingStatus = (() => {
    switch (loading) {
      case 'adr':
        return LOADING_STATUS[0];
      case 'ade':
        return LOADING_STATUS[1];
      case 'refetch-navigation':
      case 'complete':
        return LOADING_STATUS[2];
      default:
        return null;
    }
  })();

  return {
    displayPercentage,
    blurbText,
    loadingStatus,
  };
}

export default function AltMessageScreen({
  message,
  showFooter,
  loading,
}: AltMessageScreenProps) {
  const dispatch = useDispatch();
  const { isUnderwriter } = useUser();
  const { displayPercentage, blurbText, loadingStatus } =
    useProgressBar(loading);

  const handleContinue = () => {
    dispatch(HomeActions.nextAlert(isUnderwriter));
  };

  return (
    <StyledContainer>
      <StyledBox as="section">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {loading && (
            <AdrAdeProgressBar
              displayPercentage={displayPercentage}
              blurbText={blurbText}
              currentLoadingStatus={loadingStatus}
            />
          )}
          <Text
            as="h2"
            sx={{
              whiteSpace: 'pre-wrap',
              textAlign: 'center',
            }}
          >
            {message}
          </Text>
        </Box>

        {showFooter && (
          <StyledClassifyFooter
            as="footer"
            width="100%"
            marginTop="auto"
            bg="white"
            borderBottomLeftRadius="6px"
            borderBottomRightRadius="6px"
          >
            <Button
              id="continueBtn"
              type="submit"
              isPrimary
              onClick={handleContinue}
            >
              Continue
            </Button>
          </StyledClassifyFooter>
        )}
      </StyledBox>
    </StyledContainer>
  );
}
